import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useFeatureFlags } from '@node-space/hooks'
import { Box, Button, Icon, RowsPerPageType } from '@node-space/ui-components'
import { CustomersDashboardSummary } from 'components/Customers/CustomersDashboardSummary'
import { DashboardCard } from 'components/DashboardCard/DashboardCard'
import { ResultsLayout } from 'components/layout/ResultsLayout'
import { Filter } from 'components/SearchAndFilter/SearchAndFilter'
import { useSearchAndFilter } from 'components/SearchAndFilter/useSearchAndFilter'
import { PathNames } from 'constants/General'
import { useCustomersQuery } from 'hooks/queries/Customers/useCustomersQuery'
import { useCustomerStatusCounts } from 'hooks/queries/Customers/useCustomerStatusCounts'
import { useDashboardCards } from 'hooks/useDashboardCards'
import { usePaginationPagingPersist } from 'hooks/usePaginationPagingPersist'
import { EMBEDDED_CUSTOMER_STATUSES } from 'pages/EmbeddedCustomers/helpers/constants'
import { EmbeddedCustomerStatus } from 'pages/EmbeddedCustomers/helpers/types'
import { getCustomerStatusLabel, getCustomerTypeLabel } from 'pages/EmbeddedCustomers/helpers/utils'
import { StatusLabelMapping } from 'pages/NestedCustomers/constants'
import { Customer, CustomerEntityType, CustomersFilters, CustomerStatus } from 'types/customers'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { AddCustomerCta } from '../AddCustomer/AddCustomerCta'
import { CustomerModel, CustomerModels } from '../helpers/types'
import { useCustomersQueryParams } from '../hooks/useCustomersQueryParams'
import { CustomerItems } from './CustomerItems'
import { CustomersV1FiltersModal } from './CustomersV1FiltersModal'

const INITIAL_SEARCH = ''
const INITIAL_FILTERS: CustomersFilters = {}

const NON_VERIFIED_STATUSES = Object.values(EMBEDDED_CUSTOMER_STATUSES).filter(
  s => s !== EMBEDDED_CUSTOMER_STATUSES.VERIFIED
)

export interface CustomersListProps {
  customerModel: CustomerModel
  hideEpcDashboard?: boolean
  hideAddButton?: boolean
}

export const CustomersList = ({
  customerModel,
  hideEpcDashboard = false,
  hideAddButton = false,
}: CustomersListProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.CUSTOMERS, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const {
    enableCustomerFeeSetup,
    enableEpcDashboard: featureFlagEpcDashboard,
    enableRelianceBusinessFields,
    enablePaymentLimitsManagement,
  } = useFeatureFlags()
  const enableEpcDashboard = featureFlagEpcDashboard && !hideEpcDashboard
  const { activeCard, handleCardClick, dashboardCards } = useDashboardCards()
  const {
    searchValue,
    appliedSearch,
    appliedFilters,
    hasSearchAndFilter,
    setSearchValue,
    setAppliedFilters,
    onFilterChange,
    onResetAll,
  } = useSearchAndFilter(INITIAL_SEARCH, INITIAL_FILTERS)

  const { queryParams, onChangePage, onChangeRowsPerPage, onChangeSearchAndFilter } =
    useCustomersQueryParams(customerModel)

  const {
    data,
    isFetching,
    isError,
    refetch: refetchCustomers,
  } = useCustomersQuery<Customer>(queryParams)

  const {
    totals: queueTotals,
    loading: queueLoading,
    errors: queueErrors,
  } = useCustomerStatusCounts(customerModel, enableEpcDashboard)
  const customersData = data?.content || []
  const pagination = data?.pagination

  const { paging, showPagination } = usePaginationPagingPersist(pagination)

  const hasPagination = useMemo(() => !!paging && !showPagination, [paging, showPagination])
  const isEmbedded = useMemo(() => customerModel === CustomerModels.EMBEDDED, [customerModel])
  const isReliance = useMemo(() => customerModel === CustomerModels.RELIANCE, [customerModel])

  const statusFilters = useMemo(() => {
    const allStatuses = Object.values(
      isEmbedded || isReliance ? EMBEDDED_CUSTOMER_STATUSES : CustomerStatus
    )
    return hideEpcDashboard
      ? allStatuses.filter(s => s !== EMBEDDED_CUSTOMER_STATUSES.VERIFIED)
      : allStatuses
  }, [isEmbedded, isReliance, hideEpcDashboard])

  const getStatusLabel = (status: string) => {
    return isEmbedded || isReliance
      ? getCustomerStatusLabel(status as EmbeddedCustomerStatus, t)
      : StatusLabelMapping?.[status as keyof typeof StatusLabelMapping]
  }

  const AddButton = useMemo(() => {
    if (hideAddButton || (isReliance && !enableRelianceBusinessFields)) return null
    return <AddCustomerCta customerModel={customerModel} refetchCustomers={refetchCustomers} />
  }, [hideAddButton, isReliance, enableRelianceBusinessFields, customerModel, refetchCustomers])

  const FeeSetupButton = enableCustomerFeeSetup ? (
    <Button
      variant="secondary"
      onClick={() => navigate(PathNames.CUSTOMER_FEE_SETUP)}
      iconElement={<Icon name="SlidersHorizontal" />}
    >
      {t('customers.feeSetup.title')}
    </Button>
  ) : null

  useEffect(() => {
    onChangeSearchAndFilter({ search: appliedSearch })
  }, [appliedSearch, onChangeSearchAndFilter])

  const filters = useMemo(() => {
    const activeCardStatus = activeCard
      ? dashboardCards?.find(card => card?.step === activeCard)?.status
      : null

    return {
      models: [customerModel],
      ...(hideEpcDashboard ? { statuses: NON_VERIFIED_STATUSES } : {}),
      ...(appliedFilters ?? {}),
      ...(activeCardStatus ? { statuses: [activeCardStatus] } : {}),
    }
  }, [appliedFilters, activeCard, dashboardCards, customerModel, hideEpcDashboard])

  useEffect(() => {
    onChangeSearchAndFilter({ filters })
  }, [filters, onChangeSearchAndFilter])

  const totalCustomersOnboarded = useMemo(() => {
    if (!enableEpcDashboard || !isEmbedded) return 0
    const isLoadingQueues = Object.values(queueLoading).some(Boolean)
    if (isLoadingQueues) return 0
    return queueTotals?.[EMBEDDED_CUSTOMER_STATUSES.VERIFIED] ?? 0
  }, [enableEpcDashboard, isEmbedded, queueLoading, queueTotals])

  const hasQueueErrors = useMemo(() => Object.values(queueErrors).some(Boolean), [queueErrors])

  const dashboardCardsData = useMemo(
    () =>
      enableEpcDashboard && isEmbedded && !hasQueueErrors
        ? {
            cards: (dashboardCards || []).map(card => ({
              ...card,
              isActive: activeCard === card?.step,
              queue: Object.values(queueLoading).some(Boolean)
                ? '0'
                : String(queueTotals[card?.status ?? ''] ?? 0),
            })),
            onCardClick: handleCardClick,
            loading: Object.values(queueLoading).some(Boolean),
          }
        : undefined,
    [
      enableEpcDashboard,
      isEmbedded,
      hasQueueErrors,
      dashboardCards,
      queueLoading,
      queueTotals,
      activeCard,
      handleCardClick,
    ]
  )

  const introduction = enableEpcDashboard && isEmbedded && !hasQueueErrors && (
    <CustomersDashboardSummary totalCustomersOnboarded={totalCustomersOnboarded} />
  )

  const headerDescription =
    isEmbedded || isReliance ? t('embeddedCustomers.description') : t('customers.description')

  const MassCustomerLimitsButton = enablePaymentLimitsManagement ? (
    <Button variant="secondary" onClick={() => navigate(PathNames.MASS_CUSTOMER_LIMITS)}>
      {t('customers.massCustomerLimits')}
    </Button>
  ) : null

  const header = hideEpcDashboard
    ? {
        description: headerDescription,
        renderRightSection: (
          <Box flex gapX={12}>
            {MassCustomerLimitsButton}
            {AddButton}
          </Box>
        ),
      }
    : {
        description: headerDescription,
        renderRightSection: (
          <Box flex gapX={12}>
            {FeeSetupButton}
            {MassCustomerLimitsButton}
            {AddButton}
          </Box>
        ),
      }

  const dashboardCardsComponent = enableEpcDashboard && isEmbedded && !hasQueueErrors && (
    <>
      {dashboardCardsData && !dashboardCardsData.loading && (
        <Box className="flex flex-row justify-between gap-x-6 pb-2">
          {dashboardCardsData.cards.map(card => (
            <DashboardCard
              key={card.step}
              title={card.title}
              step={card.step}
              onClick={dashboardCardsData.onCardClick}
              queue={card.queue}
              type={card.type}
              isActive={card.isActive}
            />
          ))}
        </Box>
      )}
    </>
  )

  return (
    <ResultsLayout
      testId="customers-results"
      isLoading={isFetching}
      isError={isError}
      hasResults={!!customersData?.length}
      hasSearchAndFilter={hasSearchAndFilter}
      refetchResults={refetchCustomers}
      resetSearchAndFilters={onResetAll}
      enableEpcDashboard={enableEpcDashboard}
      introduction={introduction}
      tableHead={dashboardCardsComponent}
      emptyState={{
        title: t('embeddedCustomers.emptyStatus.headline'),
        description: isEmbedded
          ? t('embeddedCustomers.emptyStatus.descriptionAddDetails')
          : t('customers.addNewCustomerDescription'),
        PrimaryButton: hideEpcDashboard ? undefined : (AddButton ?? undefined),
      }}
      dashboardEmptyState={!dashboardCardsData?.cards?.length}
      header={header}
      searchAndFilter={{
        search: {
          placeholder: t('customers.searchPlaceholder'),
          value: searchValue,
          onChange: setSearchValue,
        },
        filters: [
          ...(enableEpcDashboard || hideEpcDashboard
            ? []
            : [
                {
                  id: 'statuses',
                  placeholder: t('status'),
                  values: statusFilters,
                  checkedValues: appliedFilters?.statuses ?? [],
                  getLabel: getStatusLabel,
                  onFilterChange,
                },
              ]),
          ...(hideEpcDashboard
            ? []
            : [
                {
                  id: 'types',
                  placeholder: t('type'),
                  values: Object.values(CustomerEntityType),
                  checkedValues: appliedFilters?.types ?? [],
                  getLabel: (type: string) => getCustomerTypeLabel(type as CustomerEntityType, t),
                  onFilterChange,
                },
              ]),
        ] as Filter[],
        onResetAll: enableEpcDashboard || hideEpcDashboard ? undefined : onResetAll,
        additionalActions: hideEpcDashboard ? (
          <CustomersV1FiltersModal
            statusFilters={statusFilters}
            appliedFilters={appliedFilters}
            isEmbeddedOrReliance={isEmbedded || isReliance}
            onApply={newFilters => setAppliedFilters(prev => ({ ...prev, ...newFilters }))}
            onResetAll={onResetAll}
          />
        ) : undefined,
      }}
      {...(hasPagination && {
        pagination: {
          page: queryParams?.page ?? 0,
          total: paging?.total ?? 0,
          rowsPerPage: queryParams?.size as RowsPerPageType,
          onChangePage,
          onChangeRowsPerPage,
        },
      })}
    >
      <CustomerItems customerModel={customerModel} customers={customersData} />
    </ResultsLayout>
  )
}
